/**
 * @copyright (C) 2023, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Heeseok Shin (hsshin@rsupport.com)
 *
 * @description
 *
 * @created at Wed Apr 19 2023
 **/

import React, { useEffect, useState } from "react";
import { sortBy } from "lodash-es";

import { EventHandler } from "@rfice/eventhandler";
import { Button, Column, Divider, ModalPopup, Typography, useModal } from "@/frameworks/designStuff/components";
import { useProperties } from "@/frameworks/libraries/properties";
import { _T } from "@rfice/language";

import { useSpace } from "@/frameworks/core/API";
import { EventID, ModalID } from "@/frameworks/core/commons";
import { useCreateSpaceState } from "@/frameworks/core/hooks";
import { SessionData } from "@/frameworks/libraries/storage";
import SpaceCardItem from "./private/SpaceCardItem";
import * as S from "./private/emotion";

interface SelectSpaceProps extends React.HTMLAttributes<HTMLDivElement> {
	onHide?: () => void;
	data: {
		isUpdateMode?: boolean;
		selectedSpaceThemeId: string;
	};
	type: string;
	title?: string;
}

type spaceMapType = {
	spaceThemeId: string;
	imageSrc: string;
	name: string;
	maxUserCount: number;
};

export default function SelectSpace({ onHide, data, type, title }: SelectSpaceProps) {
	const properties = useProperties();
	const modal = useModal();
	const [space, setSpace] = useState<spaceMapType | null>(null);
	const { state } = useCreateSpaceState();
	const [listMap, setListMap] = useState<SwaggerModels.SpaceThemeResponseVO[]>();

	const spaceAPI = useSpace({
		onSuccess() {
			const data = spaceAPI.spaceTheme.list.result.data?.content;
			setListMap(data);
		}
	});

	const handleChangeTheme = ({
		spaceThemeId,
		imageSrc,
		name,
		maxUserCount
	}: {
		spaceThemeId: string;
		imageSrc: string;
		name: string;
		maxUserCount: number;
	}) => {
		setSpace({
			spaceThemeId,
			imageSrc,
			name,
			maxUserCount
		});
	};

	const handleUpdateData = (data: typeof space) => {
		if (!data) return;
		SessionData.set("tis", data.imageSrc);
		SessionData.set("st", data.name);
		SessionData.set("si", data.spaceThemeId);
		EventHandler.emit(EventID.UPDATE_CREATE_SPACE, {
			type: "update",
			payload: {
				spaceThemeName: data.name,
				imageSrc: data.imageSrc,
				spaceThemeId: data.spaceThemeId,
				maxUserCount: data.maxUserCount
			}
		});
	};

	const handleClick = (evt: React.MouseEvent<HTMLButtonElement>) => {
		evt.preventDefault();

		if (evt.currentTarget.dataset["key"] === "cancel") {
			onHide && onHide();
		}

		if (evt.currentTarget.dataset["key"] === "confirm") {
			if (space) {
				//CHANGE_SELECT_SPACE_THEME
				if (data?.isUpdateMode) {
					modal.show({
						id: ModalID.ALERT_SPACE_THEME_UPDATE,
						content: (
							<ModalPopup
								modalID={ModalID.ALERT_SPACE_THEME_UPDATE}
								cancelText={_T("취소")}
								confirmText={_T("확인")}
								onCancel={() => {
									modal.hide(ModalID.ALERT_SPACE_THEME_UPDATE);
								}}
								onConfirm={() => {
									handleUpdateData(space);
									onHide?.();
									modal.hide(ModalID.ALERT_SPACE_THEME_UPDATE);
								}}
							>
								<Column horizonAlign="center">
									<Typography typoSize="Body3">
										{_T("{{name}}을 선택하였습니다.", { name: space.name })}
									</Typography>
									<Typography typoSize="Body3">
										{_T("맵 변경시에는 3D 스페이스 사용자들 환경이 변경되어 재접속됩니다.")}
									</Typography>
									<Typography typoSize="Body3">
										{_T("맵변경 후 스페이스 설정에서 적용을 하셔야 최종 반영됩니다.")}
									</Typography>
								</Column>
							</ModalPopup>
						)
					});
				} else {
					handleUpdateData(space);
					onHide?.();
				}
			}
		}
	};

	const handleListMap = async () => {
		if (type === "SPACE") {
			spaceAPI.spaceTheme.list.execute({
				spaceThemeType: "SPACE",
				page: 1,
				pageSize: 100,
				sortItem: "SORT_SEQ",
				order: "DESC",
				excel: false
			});
		} else {
			spaceAPI.spaceTheme.list.execute({
				spaceThemeType: "MEETING",
				page: 1,
				pageSize: 100,
				sortItem: "SORT_SEQ",
				order: "DESC",
				excel: false
			});
		}
	};
	useEffect(() => {
		handleListMap();
	}, []);

	return (
		<S.Wrapper>
			<S.Header horizonAlign="center" verticalAlign="center" css={[{ width: "100%", position: "relative" }]}>
				{title && <Typography typoSize="Head6">{title}</Typography>}
				<Button.Icon
					name="Close24"
					size="large"
					variant="text"
					css={{
						position: "absolute",
						right: "12px",
						top: "22px"
					}}
					onClick={handleClick}
					data-key="cancel"
				/>
			</S.Header>
			<Divider type="line" />
			<S.CardList column={4} gridGap={{ row: 20, column: 20 }}>
				{sortBy(listMap, (item) =>
					item.id === (state.imageSrc ? state?.spaceThemeId : data.selectedSpaceThemeId) ? 0 : 1
				)?.map((item, index) => (
					<SpaceCardItem
						key={index}
						onClick={() =>
							handleChangeTheme({
								spaceThemeId: item.id,
								imageSrc: item.thumbnailPath,
								name: item.name,
								maxUserCount: item.maxUserCount
							})
						}
						checked={space?.spaceThemeId === item.id}
						data={{
							thumbnails: item.thumbnails,
							spaceSceneVersion: item.spaceSceneVersion,
							spaceScenePath: item.spaceScenePath,
							selectedSpaceThemeId: state.imageSrc ? state?.spaceThemeId : data.selectedSpaceThemeId,
							maxUserCount: item.maxUserCount,
							id: item.id,
							name: item.name,
							thumbnailPath: `${properties.default.webDomain}${item.thumbnailPath}`,
							videoThumbnailPath: `${item.videoThumbnailPath}`,
							spaceSceneObject: {
								sceneLevel: "",
								name: ""
							},
							useMeeting: true, //TODO 확인 필요
							useSpace: true
						}}
					/>
				))}
			</S.CardList>
			<Divider type="line" />
			<S.Footer horizonAlign="center" verticalAlign="center" gap={8}>
				<S.ButtonStyle filled="secondary" onClick={handleClick} data-key="cancel">
					{_T("취소")}
				</S.ButtonStyle>
				<S.ButtonStyle
					onClick={handleClick}
					data-key="confirm"
					disabled={!space?.spaceThemeId || space?.spaceThemeId === data.selectedSpaceThemeId}
				>
					{data?.isUpdateMode ? _T("변경") : _T("적용하기")}
				</S.ButtonStyle>
			</S.Footer>
		</S.Wrapper>
	);
}
